/**
 * Front API client — Data Accuracy V2 (#359 + #363).
 * ⚠ ZÉRO valeur en dur — chaque champ vient des endpoints backend.
 */
import { apiClient } from '@/lib/apiClient'

export interface NationalPoint {
  weekIso: string
  visits: number
  proven: number
  proofPct: number
  qualityPass: number
  qualityEvaluable: number
  qualityPct: number
  gapPts: number
}
export interface NationalSeriesResponse { series: NationalPoint[] }

export interface NationalBreakdown {
  weekIso: string
  weekMin: string
  weekMax: string
  visits: number
  proof: { PROVEN: number; ECHEC_MERCH: number; IMPOSSIBLE_EQUIPEMENT: number; IMPOSSIBLE_REFERENTIEL: number }
  quality: {
    z1_fail: number
    z2_fail: number
    z3_fail: number
    ctrl: Record<string, { evaluable: number; fail: number }>
  }
}

export interface MerchScoreRow {
  merchCode: string | null
  visits: number
  proofPct: number
  qualityPct: number
  ec: number
  ieq: number
  iref: number
  z1Fail: number
  z2Fail: number
  z3Fail: number
  dominantZone: 'Z1' | 'Z2' | 'Z3'
  /** Wilaya la plus visitée par ce merch sur la fenêtre (peut être null si aucun PDV rattaché à une wilaya). */
  dominantWilaya: string | null
  /**
   * Canaux effectivement observés pour ce merch sur la fenêtre (déduits des
   * observations.channel_type, doctrine D-CANAL-DU-RELEVÉ). Un merch est
   * MIXTE quand `channels.length >= 2` : le front badge la carte en vue
   * canalisée pour rappeler que le score affiché est la part sur CE canal.
   * Vague dettes 2 (2026-07-27).
   */
  channels: ChannelType[]
}
export interface MerchsTableResponse {
  weekMin: string
  weekMax: string
  rows: MerchScoreRow[]
  orphans: {
    visits: number
    proven: number
    ec: number
    ieq: number
    iref: number
    qualityPass: number
    qualityEvaluable: number
  }
  /**
   * P2-bis : seuil « faible effectif ». Le front signale (badge visuel) les
   * merchs sous ce seuil sans les exclure ni les cacher. Servi par le back.
   */
  lowSampleThreshold: number
}

export interface MerchProfile {
  merchCode: string
  visits: number
  proofPct: number
  qualityPct: number
  gapPts: number
  proof: { PROVEN: number; ECHEC_MERCH: number; IMPOSSIBLE_EQUIPEMENT: number; IMPOSSIBLE_REFERENTIEL: number }
  echecKind: { GPS: number; PHOTO: number; GPS_PHOTO: number }
  dominantWilaya: string | null
  weekMin: string
  weekMax: string
}

export interface MerchHistoryResponse {
  merchCode: string
  series: NationalPoint[]
}

export interface FailingVisit {
  visitId: string
  pdvId: string
  pdvCode: string
  visitDate: string
  proofStatus: string
  qualityPass: boolean
  z1Pass: boolean
  z2Pass: boolean
  z3Pass: boolean
  gpsM: number | null
  photoPresent: boolean
  echecKind: 'GPS' | 'PHOTO' | 'GPS+PHOTO' | null
  aberrant: boolean
}
export interface FailingVisitsResponse {
  merchCode: string
  weekMin: string
  weekMax: string
  rows: FailingVisit[]
  aberrantCount: number
  aberrantThresholdM: number
}

// D-DATA-ACCURACY-V2-CANAL (#367) — filtre canal aligné sur les 8 endpoints.
// Défaut absent = « Tous » (retail + wholesale + orphelines). Retail ou Wholesale
// filtrent via `merchandiser.channel_type` (gravure #327).
export type ChannelType = 'retail' | 'wholesale'
function chanQs(c?: ChannelType): string { return c ? `&channelType=${c}` : '' }

// D-DATA-ACCURACY-V2-P3-ENTONNOIR (#366) — deux entonnoirs en RELEVÉS.
// D-QUALITE-DEUX-TAUX (2026-07-25) : marches Qualité portent `evaluable` pour
// exposer PASS + FAIL + NA + le taux réel (base évaluables). Le count marche
// reste le TAUX INDULGENT (NA→PASS, base all_visits) — les deux sont affichés
// côte à côte, l'écart révèle les non-mesurables (photos manquantes W26 : 617).
export interface FunnelStep {
  key: string
  label: string
  count: number
  /**
   * Décomposition PASS/FAIL/NA du contrôle de cette marche (Qualité seulement).
   * Absent sur PREUVE et « Visité ». Le front affiche 2 taux :
   *  - indulgent = count / base   (NA→PASS, la règle Data Quality V2)
   *  - réel      = pass / (pass + fail)  (évaluables uniquement)
   * ÉCART = les NA.
   */
  evaluable?: {
    pass: number
    fail: number
    na: number
    /** (pass + na) / base — INDÉPENDANT du cumul entonnoir. Règle NA→PASS. */
    indulgentPct: number
    /** pass / (pass + fail). 0 si aucun évaluable. */
    realPct: number
  }
  /**
   * Compte brut indépendant sur ce critère (sans filtres amont). Absent pour
   * la marche initiale de chaque entonnoir.
   */
  rawIndependent?: number
  /**
   * Marche « référence » (Planifié pour PREUVE) : visible comme repère mais
   * n'entre PAS dans le calcul du fond de l'entonnoir. Le front n'affiche
   * pas son % ni sa chip de perte.
   */
  isReference?: boolean
  pctFromStart: number
  lossFromPrev?: { n: number; pts: number }
  delta?: { count: number; pts: number }
}
export interface FunnelPreuve {
  steps: FunnelStep[]
  horsPlan: { count: number }
  /**
   * Décomposition de la marche « Visité » (au plan / hors plan) + taux de
   * respect du plan (visite in_plan / planifie). L'info du plan est en méta
   * puisque la base des marches est maintenant `all_visits`.
   *
   * ⚠ EN VUE CANALISÉE : `planifieApplicable=false`. Champs `planifie`,
   * `inPlan`, `tauxRespectPlan` mis à 0 côté back (routing sans canal). Le
   * front doit MASQUER ces champs et la marche Planifié.
   */
  visiteBreakdown: {
    inPlan: number
    horsPlan: number
    planifie: number
    tauxRespectPlan: number
  }
  /** true en vue « Tous ». false en vue canalisée → masquer marche Planifié + vsPlanifiePct. */
  planifieApplicable: boolean
  fondReadings: { vsPlanifiePct: number; vsRelevesEffectuesPct: number }
}
export interface FunnelQualite {
  steps: FunnelStep[]
  /**
   * D-QUALITE-CHAINE-REELLE (2026-07-25) — le pied entonnoir Qualité affiche
   * deux lectures, comme Preuve :
   *  - chaineReellePct (primary) = les 6 contrôles PASS strict / total visites
   *    (le RÉEL de la chaîne, ce que l'entonnoir mesure vraiment)
   *  - vsRelevesEvaluablesPct    = Data Quality carte (indulgent, NA→PASS)
   *  - vsVisitePct               = lecture historique in-plan (secondary hérité)
   */
  fondReadings: { vsVisitePct: number; vsRelevesEvaluablesPct: number; chaineReellePct: number }
}
export interface FunnelImportBadge {
  active: boolean
  anchorWeek: string
  ratioVsMedian: number
  thresholdPct: number
  medianRefWeeks: number
}
export interface FunnelResponse {
  weekMin: string
  weekMax: string
  merchCode: string | null
  channelType: ChannelType | null
  weeksInWindow: number
  preuve: FunnelPreuve
  qualite: FunnelQualite
  importPartial: FunnelImportBadge | null
}

const base = '/data-accuracy-v2'

export async function fetchNationalSeries(weekMin: string, weekMax: string, channelType?: ChannelType): Promise<NationalSeriesResponse> {
  return (await apiClient.get<NationalSeriesResponse>(`${base}/national?weekMin=${weekMin}&weekMax=${weekMax}${chanQs(channelType)}`)).data
}
export async function fetchNationalBreakdown(weekMin: string, weekMax: string, channelType?: ChannelType): Promise<NationalBreakdown> {
  return (await apiClient.get<NationalBreakdown>(`${base}/national/breakdown?weekMin=${weekMin}&weekMax=${weekMax}${chanQs(channelType)}`)).data
}
export async function fetchMerchsTable(weekMin: string, weekMax: string, channelType?: ChannelType): Promise<MerchsTableResponse> {
  return (await apiClient.get<MerchsTableResponse>(`${base}/merchs?weekMin=${weekMin}&weekMax=${weekMax}${chanQs(channelType)}`)).data
}
export async function fetchMerchProfile(code: string, weekMin: string, weekMax: string, channelType?: ChannelType): Promise<MerchProfile> {
  return (await apiClient.get<MerchProfile>(`${base}/merchs/${encodeURIComponent(code)}/profile?weekMin=${weekMin}&weekMax=${weekMax}${chanQs(channelType)}`)).data
}
export async function fetchMerchHistory(code: string, weekMin: string, weekMax: string, channelType?: ChannelType): Promise<MerchHistoryResponse> {
  return (await apiClient.get<MerchHistoryResponse>(`${base}/merchs/${encodeURIComponent(code)}/history?weekMin=${weekMin}&weekMax=${weekMax}${chanQs(channelType)}`)).data
}
export async function fetchFailingVisits(merch: string, weekMin: string, weekMax: string, channelType?: ChannelType): Promise<FailingVisitsResponse> {
  return (await apiClient.get<FailingVisitsResponse>(`${base}/failing-visits?merch=${encodeURIComponent(merch)}&weekMin=${weekMin}&weekMax=${weekMax}${chanQs(channelType)}`)).data
}

export async function fetchDaV2Funnel(weekMin: string, weekMax: string, merch?: string, channelType?: ChannelType): Promise<FunnelResponse> {
  const suffix = merch ? `&merch=${encodeURIComponent(merch)}` : ''
  return (await apiClient.get<FunnelResponse>(`${base}/funnel?weekMin=${weekMin}&weekMax=${weekMax}${suffix}${chanQs(channelType)}`)).data
}
